import React from 'react';
import { motion } from 'framer-motion';

const Donate = () => {
  const handleDonateClick = () => {
    window.location.href = 'https://buy.stripe.com/cNi00jgkzcke6nI3Ls38400';
  };

  return (
    <section id="donate" className="relative py-24 bg-gradient-to-br from-black via-neutral-900 to-black overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-dot-black opacity-30"></div>
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">
              Make a Difference
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-600 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Your donation directly impacts lives in Nepal's most remote villages. Every contribution helps us provide women empowerment, mental health support, and emergency relief.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Donation Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="glass rounded-3xl p-8 md:p-10 text-center">
              <h3 className="text-3xl font-bold text-white mb-8">
                Support Our Mission
              </h3>

              <p className="text-neutral-300 text-lg mb-8">
                Your donation supports rural women empowerment, mental health and awareness, and emergency relief (including water filter distribution) in Nepal's most remote villages.
              </p>

              <button 
                onClick={handleDonateClick}
                className="w-full relative px-8 py-6 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold text-xl rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/25"
              >
                <span className="relative z-10">Donate Now</span>
              </button>

              {/* Security Notice */}
              <div className="mt-6 text-center">
                <div className="flex items-center justify-center text-sm text-neutral-400">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                  Secure donation processing via Stripe
                </div>
              </div>
            </div>
            
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-amber-600/20 to-yellow-600/20 blur-xl opacity-0 hover:opacity-100 transition-opacity duration-500 -z-10"></div>
          </motion.div>

          {/* Impact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="glass rounded-2xl p-8">
              <h4 className="text-2xl font-bold text-white mb-6">How We Use Your Donations</h4>
              <div className="space-y-6">
                {[
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                      </svg>
                    ),
                    title: "Rural Women Empowerment Program",
                    description: "Empowering rural women through education, skills training, and economic opportunities. Supporting women-led initiatives and promoting gender equality in remote villages."
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                      </svg>
                    ),
                    title: "Mental Health and Awareness Program",
                    description: "Providing mental health support and awareness programs to rural communities, breaking stigma and offering counseling services to those in need."
                  },
                  {
                    icon: (
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                      </svg>
                    ),
                    title: "Emergency Relief",
                    description: "Rapid response and support during natural disasters and emergencies, including water filter distribution. Providing immediate aid, shelter, food, clean water, and medical assistance to affected communities."
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-4"
                  >
                    <div className="text-amber-400 mt-1">{item.icon}</div>
                    <div>
                      <h5 className="text-lg font-semibold text-white mb-2">{item.title}</h5>
                      <p className="text-neutral-300 text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Transparency */}
            <div className="glass rounded-2xl p-8">
              <h4 className="text-2xl font-bold text-white mb-6">100% Transparency</h4>
              <p className="text-neutral-300 mb-6">
                We believe in complete transparency. Every donation is tracked and reported, 
                ensuring your contribution reaches those who need it most.
              </p>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-300">Program Costs</span>
                  <span className="text-amber-400 font-semibold">95%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-300">Credit card, accounting, and transfer fees</span>
                  <span className="text-amber-400 font-semibold">5%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-300">Fundraising</span>
                  <span className="text-amber-400 font-semibold">0%</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Donate; 